import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { useFollowAccount } from "@/api/accounts";
import { QueryKeys, queryClient } from "@/api/core";
import { TimelineQueryKeys } from "@/api/timelines/query-keys";
import { useApi } from "@/hooks";
import toast from "@/toast";
import {
  prependPageItem,
  removeAccountStatusesFromTimeline,
} from "@/utils/queries";

import type { Account, Relationship } from "@/schemas";

interface Variables {
  account: Account;
  params?: {
    notifications: boolean;
  };
  isSubscribed?: boolean;
}

const messages = defineMessages({
  muteSuccess: {
    id: "accounts.mute.success",
    defaultMessage: "You have successfully muted @{username}.",
  },
});

// TODO: remove statuses from cache from muted Account.

function useMuteAccount() {
  const api = useApi();
  const intl = useIntl();

  const followAccount = useFollowAccount();

  return useMutation({
    mutationFn: (variables: Variables) =>
      api.post<Relationship>(`/api/v1/accounts/${variables.account.id}/mute`, {
        params: variables.params,
      }),
    async onMutate(variables) {
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );
      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);
      const mutedAccountsQueryKey = QueryKeys.accounts.mutes();

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: relationshipQueryKey });
      await queryClient.cancelQueries({ queryKey: accountQueryKey });
      await queryClient.cancelQueries({ queryKey: mutedAccountsQueryKey });

      // Snapshot the previous values
      const prevRelationship = queryClient.getQueryData(
        relationshipQueryKey,
      ) as Relationship;
      const prevAccount = queryClient.getQueryData(accountQueryKey) as Account;
      const prevMutedAccounts = queryClient.getQueryData(
        mutedAccountsQueryKey,
      ) as Account[] | undefined;

      // Optimistically update to the new values
      const nextRelationship = { ...prevRelationship, muting: true };
      const nextAccount = { ...prevAccount, relationship: nextRelationship };

      queryClient.setQueryData(relationshipQueryKey, nextRelationship);
      queryClient.setQueryData(accountQueryKey, nextAccount);
      prependPageItem(mutedAccountsQueryKey, nextAccount);

      return { prevAccount, prevRelationship, prevMutedAccounts };
    },
    onSuccess(response, variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      removeAccountStatusesFromTimeline(
        TimelineQueryKeys.all,
        variables.account.id,
      );

      if (variables.isSubscribed) {
        followAccount.mutate({
          account: variables.account,
          params: { notify: false },
        });
      }

      toast.success(
        intl.formatMessage(messages.muteSuccess, {
          username: variables.account.acct,
        }),
      );
    },
    onError(error, variables, context) {
      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(
          QueryKeys.relationships.byAccount(variables.account.id),
          context.prevRelationship,
        );
        queryClient.setQueryData(
          QueryKeys.accounts.account(variables.account.id),
          context.prevAccount,
        );
        queryClient.setQueryData(
          QueryKeys.accounts.mutes(),
          context.prevMutedAccounts,
        );
      }
    },
  });
}

export { useMuteAccount };
